import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  RefreshCw, MessageCircle, Loader2, CheckCircle2, Send, Filter, User,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  open: "bg-amber-100 text-amber-800",
  assigned: "bg-sky-100 text-sky-800",
  in_progress: "bg-indigo-100 text-indigo-800",
  waiting_customer: "bg-purple-100 text-purple-800",
  resolved: "bg-emerald-100 text-emerald-800",
  closed: "bg-slate-200 text-slate-700",
  escalated: "bg-rose-100 text-rose-800",
};
const PRIORITY_TONE = {
  urgent: "bg-rose-100 text-rose-800",
  high: "bg-orange-100 text-orange-800",
  medium: "bg-amber-100 text-amber-800",
  low: "bg-slate-100 text-slate-700",
};
const STATUSES = ["open", "assigned", "in_progress", "waiting_customer", "resolved", "closed", "escalated"];
const CHANNELS = ["", "crm", "whatsapp", "email"];

export default function Complaints() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [statusF, setStatusF] = useState("open");
  const [channelF, setChannelF] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [resolveText, setResolveText] = useState("");
  const [noteText, setNoteText] = useState("");
  const [savingAction, setSavingAction] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { limit: 100 };
      if (statusF && statusF !== "all") params.complaint_status = statusF;
      if (channelF) params.channel = channelF;
      if (q) params.q = q;
      const { data } = await api.get(`/support/complaints`, { params });
      setRows(data?.rows || data?.items || data || []);
      setTotal(data?.total || (Array.isArray(data) ? data.length : (data?.rows?.length || 0)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load complaints");
    } finally { setBusy(false); }
  }, [statusF, channelF, q]);

  useEffect(() => { load(); }, [load]);

  const open = async (cid) => {
    try {
      const { data } = await api.get(`/support/complaints/${cid}`);
      setSelected(data);
      setResolveText("");
      setNoteText("");
      setTimeout(() => {
        document.querySelector('[data-testid="cp-detail-card"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to open"); }
  };

  const updateStatus = async (newStatus) => {
    if (!selected) return;
    setSavingAction("status");
    try {
      const { data } = await api.patch(`/support/complaints/${selected.id}`, { complaint_status: newStatus });
      setSelected(data);
      toast.success(`Status → ${newStatus}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setSavingAction(""); }
  };

  const addNote = async () => {
    if (!selected || !noteText.trim()) return;
    setSavingAction("note");
    try {
      const { data } = await api.post(`/support/complaints/${selected.id}/notes`,
                                       { text: noteText.trim(), internal: true });
      setSelected(data);
      setNoteText("");
      toast.success("Note added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Note failed"); }
    finally { setSavingAction(""); }
  };

  const resolve = async () => {
    if (!selected || !resolveText.trim()) { toast.error("Add resolution notes"); return; }
    setSavingAction("resolve");
    try {
      const { data } = await api.post(`/support/complaints/${selected.id}/resolve`,
                                       { resolution_notes: resolveText.trim() });
      setSelected(data);
      setResolveText("");
      toast.success("Complaint resolved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resolve failed"); }
    finally { setSavingAction(""); }
  };

  const close = async () => {
    if (!selected) return;
    setSavingAction("close");
    try {
      const { data } = await api.post(`/support/complaints/${selected.id}/close`);
      setSelected(data);
      toast.success("Closed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Close failed"); }
    finally { setSavingAction(""); }
  };

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><MessageCircle /> Complaints</h1>
        <p className="text-sm text-muted-foreground">Every complaint raised via WhatsApp bot, CRM, or email lands here. Assign, note, and resolve with a full audit trail.</p>
      </div>

      <Card><CardContent className="p-3 flex gap-2 items-end flex-wrap">
        <Filter size={14} className="text-slate-500 mb-2" />
        <div>
          <label className="text-[10px] text-slate-500 uppercase">Status</label>
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger className="w-40 h-9" data-testid="cp-filter-status"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase">Channel</label>
          <Select value={channelF || "all"} onValueChange={(v) => setChannelF(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 h-9" data-testid="cp-filter-channel"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All channels</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
              <SelectItem value="crm">CRM</SelectItem>
              <SelectItem value="email">Email</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[240px] flex-1 max-w-md">
          <label className="text-[10px] text-slate-500 uppercase">Search</label>
          <Input placeholder="Subject / phone / ticket..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="cp-search" />
        </div>
        <Button size="sm" onClick={load} disabled={busy} data-testid="cp-refresh">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Refresh
        </Button>
      </CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Card><CardContent className="p-3"><div className="text-xs text-slate-500">Showing</div><div className="text-2xl font-bold">{rows.length}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-xs text-slate-500">Open</div><div className="text-2xl font-bold text-amber-800">{rows.filter((r) => r.complaint_status === "open").length}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-xs text-slate-500">In progress</div><div className="text-2xl font-bold text-indigo-800">{rows.filter((r) => r.complaint_status === "in_progress").length}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-xs text-slate-500">Resolved</div><div className="text-2xl font-bold text-emerald-800">{rows.filter((r) => r.complaint_status === "resolved").length}</div></CardContent></Card>
      </div>

      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ticket</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No complaints in this filter.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`cp-row-${r.id}`}>
                <TableCell><span className="font-mono text-xs text-primary">{r.ticket_number}</span></TableCell>
                <TableCell><div className="text-sm">{r.subject}</div><div className="text-[10px] text-slate-500">{r.category}</div></TableCell>
                <TableCell><div className="text-xs flex items-center gap-1"><User size={10} /> {r.member_name || "—"}</div><div className="text-[10px] text-slate-500">{r.member_phone}</div></TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{r.channel}</Badge></TableCell>
                <TableCell><Badge className={`text-[10px] ${PRIORITY_TONE[r.priority] || "bg-slate-100"}`}>{r.priority}</Badge></TableCell>
                <TableCell><Badge className={`text-[10px] ${STATUS_TONE[r.complaint_status] || "bg-slate-100"}`}>{r.complaint_status?.replace("_", " ")}</Badge></TableCell>
                <TableCell className="text-xs">{(r.created_at || "").slice(0, 10)}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => open(r.id)} data-testid={`cp-open-${r.id}`}>Open</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {selected && (
        <Card data-testid="cp-detail-card"><CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-2 flex-wrap">
            <div>
              <div className="text-xs text-slate-500">{selected.ticket_number}</div>
              <h2 className="text-lg font-semibold">{selected.subject}</h2>
              <div className="text-xs text-slate-500 mt-1">
                {selected.category} • {selected.channel} • Created {(selected.created_at || "").slice(0, 16).replace("T", " ")}
                {selected.member_name && ` • ${selected.member_name} (${selected.member_phone || ""})`}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Badge className={`text-[10px] ${PRIORITY_TONE[selected.priority] || "bg-slate-100"}`}>{selected.priority}</Badge>
              <Badge className={`text-[10px] ${STATUS_TONE[selected.complaint_status] || "bg-slate-100"}`}>{selected.complaint_status?.replace("_", " ")}</Badge>
              <Button size="sm" variant="ghost" onClick={() => setSelected(null)}>Close</Button>
            </div>
          </div>

          <div className="rounded border bg-slate-50 p-3 text-sm whitespace-pre-wrap">{selected.description}</div>

          {/* Status quick actions */}
          <div className="flex flex-wrap gap-2">
            {STATUSES.filter((s) => !["resolved", "closed"].includes(s)).map((s) => (
              <Button key={s} size="sm" variant={selected.complaint_status === s ? "default" : "outline"}
                      onClick={() => updateStatus(s)} disabled={savingAction === "status"}
                      data-testid={`cp-set-${s}`}>
                {s.replace("_", " ")}
              </Button>
            ))}
          </div>

          {/* Notes */}
          {selected.internal_notes?.length > 0 && (
            <div className="space-y-1.5 border-t pt-3">
              <div className="text-xs uppercase text-slate-500 font-semibold">Internal notes</div>
              {selected.internal_notes.map((n) => (
                <div key={n.id} className="text-xs border-l-2 border-primary/40 bg-slate-50 pl-2 py-1">
                  <div className="text-[10px] text-slate-500">{n.author || "staff"} • {(n.at || "").slice(0, 16).replace("T", " ")}</div>
                  <div>{n.text}</div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-[10px] text-slate-500 uppercase">Add internal note</label>
              <Textarea rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Note visible only to staff…" data-testid="cp-note-input" />
            </div>
            <Button size="sm" onClick={addNote} disabled={savingAction === "note" || !noteText.trim()} data-testid="cp-note-btn">
              {savingAction === "note" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Add
            </Button>
          </div>

          {/* Resolve */}
          <div className="border-t pt-3 space-y-2">
            <label className="text-[10px] text-slate-500 uppercase">Resolution notes (customer-facing)</label>
            <Textarea rows={2} value={resolveText} onChange={(e) => setResolveText(e.target.value)}
                      placeholder="What was done to fix this?" data-testid="cp-resolve-input" />
            <div className="flex gap-2">
              <Button size="sm" onClick={resolve} disabled={savingAction === "resolve" || !resolveText.trim() || selected.complaint_status === "resolved" || selected.complaint_status === "closed"}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cp-resolve-btn">
                {savingAction === "resolve" ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />} Mark Resolved
              </Button>
              <Button size="sm" variant="outline" onClick={close}
                      disabled={savingAction === "close" || selected.complaint_status === "closed"} data-testid="cp-close-btn">
                Close ticket
              </Button>
            </div>
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}
